import { Box, Button, Chip, Typography } from "@mui/material";
import lodash from "lodash";
import { useState } from "react";
import {
  BentoBrand,
  MetadataLocationType,
  MetadataType,
  SearchParams,
} from "schemas/dashboard";

import { useAutoCompletePrediction } from "components/LocationAutocomplete/useAutoCompletePrediction";
import { DiscoverSearchChipColor } from "utils/color";
import { useSearchBrand } from "utils/useSearchBrand";

import styles from "./styles";

interface Props {
  selectedBrand: BentoBrand;
}

const MAX_TAGS = 3;

const BrandLocations = ({ selectedBrand }: Props) => {
  const { handleClickOnTag, handleAddGeopoint } = useSearchBrand();
  const { setInputValue } = useAutoCompletePrediction(handleAddGeopoint);
  const [expanded, setExpanded] = useState(false);

  const renderChip = (
    index: number,
    location: string,
    locationType?: MetadataLocationType,
  ) => {
    return (
      <Chip
        key={index}
        size="small"
        sx={{
          backgroundColor:
            DiscoverSearchChipColor[SearchParams.LOCATION_DISCOVER],
          fontSize: 11,
          mx: 0.25,
        }}
        label={lodash.startCase(location)}
        onClick={(e) =>
          handleClickOnTag(
            e,
            {
              type: MetadataType.location,
              value: lodash.startCase(location),
              locationType,
            },
            setInputValue,
          )
        }
      />
    );
  };

  const getLocation = () => {
    if (
      selectedBrand?.isInternational &&
      selectedBrand?.countries &&
      selectedBrand?.countries?.length > 0
    ) {
      const location = selectedBrand?.countries?.[0];
      return {
        name: "Country of Origin",
        value: [
          renderChip(0, location, MetadataLocationType.country),
          <Chip
            key={1}
            size="small"
            sx={{
              backgroundColor:
                DiscoverSearchChipColor[SearchParams.LOCATION_DISCOVER],
              fontSize: 11,
              mx: 0.25,
            }}
            label="International"
          />,
        ],
      };
    }

    if (
      selectedBrand?.locationNames &&
      selectedBrand.locationNames.length > 0
    ) {
      const locationNamesChips = selectedBrand?.locationNames?.map(
        (location, index) => renderChip(index, location),
      );
      return { name: "Location", value: locationNamesChips };
    }
    if (selectedBrand?.countries && selectedBrand.countries.length > 0) {
      const countriesChips = selectedBrand?.countries.map((location, index) =>
        renderChip(index, location, MetadataLocationType.country),
      );
      return { name: "Location", value: countriesChips };
    }

    if (selectedBrand?.continents && selectedBrand?.continents.length > 0) {
      const continentChips = selectedBrand?.continents.map((location, index) =>
        renderChip(index, location, MetadataLocationType.continent),
      );
      return { name: "Location", value: continentChips };
    }
    return { name: "", value: <></> };
  };

  const { name, value } = getLocation();

  const renderedChips = expanded ? (
    value
  ) : Array.isArray(value) && value?.length > MAX_TAGS ? (
    <>
      {value.slice(0, MAX_TAGS)}{" "}
      <Button
        size="small"
        sx={styles.moreButton}
        onClick={() => setExpanded(true)}
      >
        + {value?.length - MAX_TAGS} more{" "}
      </Button>{" "}
    </>
  ) : (
    value
  );

  return (
    <Typography component="div" gutterBottom>
      {name && (
        <>
          <Box component="span" sx={styles.key}>
            {name}:{" "}
          </Box>{" "}
          <Box component="span">{renderedChips}</Box>
        </>
      )}
    </Typography>
  );
};

export default BrandLocations;
